import { useEffect, useRef, useState, useCallback } from 'react'
import { X, GripHorizontal, Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import BottomSheet from './BottomSheet'

const STORAGE_PREFIX = 'draggable_panel_pos_'

function loadPosition(id, defaultPosition) {
  if (!id || typeof window === 'undefined') return defaultPosition
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${id}`)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        return parsed
      }
    }
  } catch {}
  return defaultPosition
}

function savePosition(id, pos) {
  if (!id || typeof window === 'undefined') return
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(pos))
  } catch {}
}

export default function DraggablePanel({
  id,
  title,
  onClose,
  children,
  defaultPosition = { x: 8, y: 60 },
  width = 'w-72',
  className = '',
  loading = false,
}) {
  const isMobile = useIsMobile()
  const panelRef = useRef(null)
  const isDragging = useRef(false)
  const dragOrigin = useRef({ mx: 0, my: 0, px: 0, py: 0 })
  const [pos, setPos] = useState(() => loadPosition(id, defaultPosition))

  const clampPosition = useCallback((next) => {
    const panel = panelRef.current
    if (!panel) return next
    const parent = panel.offsetParent
    if (!parent) return next
    const maxX = Math.max(0, parent.clientWidth - panel.offsetWidth)
    const maxY = Math.max(0, parent.clientHeight - panel.offsetHeight)
    return {
      x: Math.max(0, Math.min(maxX, next.x)),
      y: Math.max(0, Math.min(maxY, next.y)),
    }
  }, [])

  useEffect(() => {
    // Asegurar que una posición guardada no quede fuera del contenedor tras resize
    setPos(prev => clampPosition(prev))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startDrag = useCallback((clientX, clientY) => {
    if (!panelRef.current) return
    isDragging.current = true
    dragOrigin.current = { mx: clientX, my: clientY, px: pos.x, py: pos.y }
  }, [pos])

  const onMouseDown = useCallback((e) => {
    startDrag(e.clientX, e.clientY)
    e.preventDefault()
  }, [startDrag])

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0]
    if (t) startDrag(t.clientX, t.clientY)
  }, [startDrag])

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return
      const clientX = e.touches ? e.touches[0]?.clientX : e.clientX
      const clientY = e.touches ? e.touches[0]?.clientY : e.clientY
      if (clientX == null || clientY == null) return
      const dx = clientX - dragOrigin.current.mx
      const dy = clientY - dragOrigin.current.my
      setPos(clampPosition({
        x: dragOrigin.current.px + dx,
        y: dragOrigin.current.py + dy,
      }))
    }
    const onUp = () => {
      if (isDragging.current) {
        isDragging.current = false
        savePosition(id, pos)
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('touchmove', onMove, { passive: false })
    document.addEventListener('touchend', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onUp)
    }
  }, [clampPosition, id, pos])

  if (isMobile) {
    return (
      <BottomSheet title={title} onClose={onClose} loading={loading}>
        {children}
      </BottomSheet>
    )
  }

  return (
    <div
      ref={panelRef}
      className={`absolute z-50 flex flex-col rounded-xl shadow-2xl border overflow-hidden select-none pointer-events-auto bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-700 ${width} ${className}`}
      style={{ left: pos.x, top: pos.y }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b cursor-grab active:cursor-grabbing bg-slate-100 dark:bg-gray-800 border-slate-200 dark:border-gray-700/60"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        <div className="flex items-center gap-1.5">
          <GripHorizontal size={12} className="text-slate-400 dark:text-gray-500" />
          <span className="text-xs font-bold text-slate-800 dark:text-white">{title}</span>
          {loading && (
            <Loader2 size={12} className="animate-spin text-blue-500" title="Calculando…" />
          )}
        </div>
        <button
          onClick={onClose}
          className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 rounded hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[80vh]">
        {children}
      </div>
    </div>
  )
}
